import {
  ChangeDetectionStrategy,
  Component, DestroyRef,
  EventEmitter, inject,
  Input,
  OnInit,
  Output,
  ViewChild,
  ElementRef,
  AfterViewInit,
  HostListener,
  OnDestroy
} from '@angular/core';
import {CurrentUser} from '../../user/current-user.service';
import {ChatbotChatService} from '../api-services/chatbot-chat.service';
import {ChatbotUserService} from '../api-services/chatbot-user.service';
import {ChatbotChat} from '../state/chatbot-chat';
import {TranslatorService} from '../../translation/translator.service';
import {ChatbotMessagePair} from '../state/chatbot-message-pair';
import {ChatbotChatEventService} from '../utility-services/chatbot-chat-event.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {filter, take} from 'rxjs/operators';
import {Observable, BehaviorSubject} from 'rxjs';
import {ChatbotProfileProviderService} from '../services/chatbot-profile-provider.service';

@Component({
  selector: 'app-chatbot-chat-selections',
  templateUrl: './chatbot-chat-selections.component.html',
  styleUrls: ['./chatbot-chat-selections.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class ChatbotChatSelectionsComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input()
    selectedChat: ChatbotChat;

  @Output()
    chatSelected: EventEmitter<ChatbotChat> = new EventEmitter<ChatbotChat>();

  @ViewChild('scrollContainer', { static: false })
    scrollContainer!: ElementRef;

  // Observable-basierte Implementierung wie in Mobile Sidebar
  public chatList$: Observable<ChatbotChat[]> = this.chatbotChatService.chats$;
  public loading$: Observable<boolean> = this.chatbotChatService.loading$;
  public pagination$ = this.chatbotChatService.pagination$;

  // Für die Gruppierung der Chats
  groupedChats: { [key: string]: ChatbotChat[] } = {};
  currentChatList: ChatbotChat[] = [];

  deleteFormOpen: boolean = false;
  isSidebarClosed: boolean = false;

  private destroyRef = inject(DestroyRef);
  private boundScrollHandler = this.onChatListScroll.bind(this);

  // Component-spezifische Loading-State für Service-State-Separation
  private componentLoading$ = new BehaviorSubject<boolean>(false);
  public readonly componentLoading = this.componentLoading$.asObservable();

  constructor(private currentUser: CurrentUser,
              public chatbotChatService: ChatbotChatService,
              private chatbotUserService: ChatbotUserService,
              private translatorService: TranslatorService,
              private chatbotChatEventService: ChatbotChatEventService,
              private chatbotProfileProviderService: ChatbotProfileProviderService) {
  }

  ngOnInit(): void {

    // Component-spezifisches Loading-State-Management
    this.componentLoading$.next(true);

    // Initialisiere Pagination-System
    this.chatbotUserService.checkChatbotUserExists().subscribe({
      next: (exists) => {
        if (exists) {
          this.chatbotChatService.resetChatList();
          this.chatbotChatService.loadInitialChats().subscribe({
            next: (chats) => {
              this.componentLoading$.next(false);
              this.updateChatTitles(chats);
            },
            error: (error) => {
              this.componentLoading$.next(false);
            }
          });
        }
        else {
          this.componentLoading$.next(false);
        }
      },
      error: (error) => {
        this.componentLoading$.next(false);
      }
    });

    // Abonniere Chat-Liste für Gruppierung
    this.chatList$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(chats => {
      this.currentChatList = chats;
      this.updateChatTitles(chats); // Update chat titles when chat list changes
      this.groupChatsByDate();

      // Nach Chat-Update prüfen ob Scrollbar vorhanden
      setTimeout(() => {
        this.updateScrollableClass();

        // Auto-Load wenn keine Scrollbar aber genug Chats
        this.checkAndTriggerAutoLoad('chatListUpdated');
      }, 100);
    });

    // Subscribe to profile updates to refresh chat titles
    this.chatbotProfileProviderService.profiles$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(profiles => {
      if (profiles && profiles.length > 0 && this.currentChatList && this.currentChatList.length > 0) {
        this.updateChatTitles(this.currentChatList);
      }
    });

    // Subscribe to markdown re-render events to update chat display
    this.chatbotProfileProviderService.markdownReRender$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.currentChatList && this.currentChatList.length > 0) {
        // Force change detection to update the display
        setTimeout(() => {
          this.groupChatsByDate();
        }, 0);
      }
    });

    // Entfernt: messageSend$ Subscription führt zu exzessiven Refreshs
    // Nur chatCreated$ Events verursachen Chat-List-Updates
    this.chatbotChatEventService.chatCreated$
      .pipe(takeUntilDestroyed(this.destroyRef), filter(value => !!value))
      .subscribe(chat => {
        this.addChatToChatList(chat);
      });
  }

  ngAfterViewInit(): void {
    // Setup Infinite Scroll für Desktop
    this.setupInfiniteScroll();
    this.updateScrollableClass();

    // Auto-Load-Check nach Initial-Setup
    setTimeout(() => {
      this.checkAndTriggerAutoLoad('ngAfterViewInit');
    }, 200);
  }

  private setupInfiniteScroll(): void {
    if (this.scrollContainer?.nativeElement) {
      // Finde den tatsächlich scrollbaren inneren Container
      const chatSelectionsElement = this.scrollContainer.nativeElement.querySelector('.chat-selections');

      if (chatSelectionsElement) {
        // Remove any existing listeners to avoid duplicates
        chatSelectionsElement.removeEventListener('scroll', this.boundScrollHandler);

        // Event-Listener am INNEREN Container anbringen
        chatSelectionsElement.addEventListener('scroll', (event: Event) => {
          this.onChatListScroll(event);
          this.updateScrollableClass();
        }, { passive: true });
      }

      // Initial prüfen, ob scrollbar
      this.updateScrollableClass();

    }
    else {
      // Retry nach kurzer Verzögerung falls ViewChild noch nicht initialisiert
      setTimeout(() => {
        if (this.scrollContainer?.nativeElement) {
          this.setupInfiniteScroll();
        }
      }, 100);
    }
  }

  // Optimierte Scroll-Methode für Chat-Listen-Container
  private onChatListScroll(event: Event): void {
    const target = event.target as HTMLElement;
    const chatSelectionsElement = this.scrollContainer?.nativeElement?.querySelector('.chat-selections');

    // Skip if loading more chats or no more chats to load
    this.chatbotChatService.pagination$.pipe(take(1)).subscribe(paginationState => {
      if (paginationState.loading || !paginationState.hasMore) {
        return;
      }

      // Prüfe, ob das Event vom richtigen Container kommt (.chat-selections)
      if (target !== chatSelectionsElement) {
        return;
      }

      const scrollElement = chatSelectionsElement as HTMLElement;
      if (!scrollElement) {
        return;
      }

      const threshold = 0.80;

      const scrollTop = scrollElement.scrollTop;
      const clientHeight = scrollElement.clientHeight;
      const scrollHeight = scrollElement.scrollHeight;

      const scrollableDistance = scrollHeight - clientHeight;
      const scrollPercentage = scrollableDistance > 0 ? (scrollTop / scrollableDistance) : 1;

      // Trigger Infinite Scroll mit Threshold am inneren Container
      if (scrollPercentage >= threshold) {
        this.loadMoreChats();
      }
    });
  }

  // Prüft, ob der Container scrollbar ist, und setzt die Klasse für Schatten
  private updateScrollableClass(): void {
    const chatSelectionsElement = this.scrollContainer?.nativeElement?.querySelector('.chat-selections') as HTMLElement;
    if (!chatSelectionsElement) {
      return;
    }

    const isScrollable = chatSelectionsElement.scrollHeight > chatSelectionsElement.clientHeight;

    if (isScrollable) {
      chatSelectionsElement.classList.add('is-scrollable');
    }
    else {
      chatSelectionsElement.classList.remove('is-scrollable');
    }
  }

  newChat(): void {
    this.selectedChat = null;
    this.chatSelected.emit(this.selectedChat);
  }

  selectChat(chat: ChatbotChat): void {
    this.selectedChat = chat;
    this.chatSelected.emit(chat);
  }

  public loadMoreChats(): void {
    this.chatbotChatService.pagination$.pipe(take(1)).subscribe(paginationState => {
      if (!paginationState.hasMore) {
        return;
      }

      if (paginationState.loading) {
        return;
      }

      this.chatbotChatService.loadNextPage().subscribe({
        next: (newChats) => {
          if (newChats.length === 0) {
            this.chatbotChatService.updatePaginationState({hasMore: false});
          }
          else {
            // Update titles for newly loaded chats
            this.updateChatTitles(newChats);
          }
        },
        error: (error) => {
          console.error('LoadNextPage failed:', error);
          this.chatbotChatService.updatePaginationState({loading: false, error});
        }
      });
    });
  }

  public refreshChatList(): void {
    // Component-spezifisches Loading-State
    this.componentLoading$.next(true);

    // Verwende debounced refreshChatList-Methode
    this.chatbotChatService.refreshChatList().subscribe({
      next: () => {
        this.componentLoading$.next(false);
      },
      error: (error) => {
        this.componentLoading$.next(false);
      }
    });
  }

  addChatToChatList(chat: ChatbotChat): void {
    // Update the title of the new chat before adding it
    this.updateChatTitles([chat]);

    this.chatbotChatService.addSingleChat(chat);
    this.selectChat(chat);
  }

  deleteChatFromChatList(chat: ChatbotChat): void {
    // Einzige Quelle der Wahrheit - nur hier wird gelöscht
    this.chatbotChatService.deleteChatByID(chat.chat_id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        // UI State aufräumen
        if (chat && this.selectedChat && chat.chat_id === this.selectedChat.chat_id) {
          this.newChat();
        }
        // Liste neu laden
        this.refreshChatList();
      },
      error: (error) => {
        console.error('Failed to delete chat:', error);
      }
    });
  }

  public trackByChatId(index: number, chat: ChatbotChat): number {
    return chat.chat_id;
  }

  sortChatListByTimestamp(): void {
    this.currentChatList.sort((a, b) => {
      // Infinity sorts empty chats to the top
      let timeA = Infinity;
      let timeB = Infinity;

      if (a.messages && a.messages.length > 0) {
        a.messages.sort((x, y) => x.question_id - y.question_id);
        const lastMessageA: ChatbotMessagePair = a.messages[a.messages.length - 1];
        timeA = new Date(lastMessageA.timestamp.valueOf()).getTime();
      }

      if (b.messages && b.messages.length > 0) {
        b.messages.sort((x, y) => x.question_id - y.question_id);
        const lastMessageB: ChatbotMessagePair = b.messages[b.messages.length - 1];
        timeB = new Date(lastMessageB.timestamp.valueOf()).getTime();
      }
      return timeB - timeA;
    });
  }

  groupChatsByDate(): void {
    this.sortChatListByTimestamp();

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const lastweek = new Date(today);
    lastweek.setDate(lastweek.getDate() - 7);

    this.groupedChats = {
      Empty: [],
      Today: [],
      Recent: [],
      Older: []
    };

    for (const chat of this.currentChatList) {
      if (chat.messages && chat.messages.length > 0) {
        const lastMessage: ChatbotMessagePair = chat.messages[chat.messages.length - 1];
        const chatDate = new Date(lastMessage.timestamp.valueOf());
        chatDate.setHours(0, 0, 0, 0);

        if (+chatDate === +today) {
          this.groupedChats.Today.push(chat);
        }
        else if (+chatDate > +lastweek) {
          this.groupedChats.Recent.push(chat);
        }
        else {
          this.groupedChats.Older.push(chat);
        }
      }
      else {
        this.groupedChats.Today.push(chat);
      }
    }
  }
  keepOrder = (a, b) => 0;

  isGroupNotEmpty(group: {key: string; value: ChatbotChat[]}): boolean {
    return group.value.length > 0 && group.key !== 'Empty';
  }

  deleteAllChats(): void {
    this.chatSelected.emit(null);
    this.chatbotChatService.deleteAllChats().subscribe(value => {
      console.log('deleteAllChats at chatbot-chat-selections', value);
      this.refreshChatList();
    });
    this.deleteFormOpen = false;
  }

  openDeleteForm(): void {
    this.deleteFormOpen = true;
  }

  closeDeleteForm(): void {
    this.deleteFormOpen = false;
  }

  toggleSidebar(): void {
    this.isSidebarClosed = !this.isSidebarClosed;
  }

  // Component-spezifisches Event-Handling mit HostListener
  @HostListener('scroll', ['$event'])
  onContainerScroll(event: Event): void {
    // Scope-Isolation: Nur auf spezifische Container reagieren
    const target = event.target as HTMLElement;

    if (!target.closest('.chat-sidebar') || target.closest('.chat-messages-container')) {
      return;
    }

    // Event-Deduplication: Verhindere mehrfache Verarbeitung
    event.stopPropagation();
  }

  ngOnDestroy(): void {
    // Cleanup von Event-Listenern am inneren Container
    const chatSelectionsElement = this.scrollContainer?.nativeElement?.querySelector('.chat-selections');
    if (chatSelectionsElement) {
      chatSelectionsElement.removeEventListener('scroll', this.boundScrollHandler);
    }

    this.componentLoading$.complete();
  }

  __(key: string): string {
    return this.translatorService.translate('chatbot-chat-selections.' + key);
  }

  // Auto-Load-Mechanismus für Lazy Loading Fix
  private checkAndTriggerAutoLoad(context: string): void {
    const chatSelectionsElement = this.scrollContainer?.nativeElement?.querySelector('.chat-selections') as HTMLElement;

    if (!chatSelectionsElement) {
      return;
    }

    const isScrollable = chatSelectionsElement.scrollHeight > chatSelectionsElement.clientHeight;
    const hasEnoughChats = this.currentChatList.length >= 6;

    // Auto-Load triggern wenn:
    // 1. Keine Scrollbar vorhanden
    // 2. Mindestens 6 Chats geladen
    // 3. Pagination hat noch mehr Chats
    // 4. Nicht bereits am Laden

    this.chatbotChatService.pagination$.pipe(take(1)).subscribe(paginationState => {
      const shouldAutoLoad = !isScrollable &&
                            hasEnoughChats &&
                            paginationState.hasMore &&
                            !paginationState.loading &&
                            this.currentChatList.length < 50; // Sicherheits-Limit

      if (shouldAutoLoad) {
        this.loadMoreChats();
        setTimeout(() => {
          this.checkAndTriggerAutoLoad(`${context}-retry`);
        }, 500);
      }
    });
  }

  private updateChatTitles(chats: ChatbotChat[]): void {
    if (!chats || chats.length === 0) {
      return;
    }

    const keycloakIds = new Set<string>();
    const acronyms = new Set<string>();

    chats.forEach(chat => {
      if (chat.title) {
        // Look for keycloak IDs in titles
        const titleIds = chat.title.match(/\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi);
        if (titleIds) {
          titleIds.forEach(id => keycloakIds.add(id));
        }

        // Look for 6-character acronyms in titles
        const titleAcronyms = chat.title.match(/\b[a-zA-Z0-9]{6}\b/g);
        if (titleAcronyms) {
          titleAcronyms.forEach(acronym => acronyms.add(acronym));
        }
      }

      if (chat.profiles) {
        chat.profiles.forEach(profile => {
          if (profile.user_id) {
            keycloakIds.add(profile.user_id);
          }
          if (profile.acronym) {
            acronyms.add(profile.acronym);
          }
        });
      }
    });

    // Replace keycloak IDs and acronyms in chat titles
    chats.forEach(chat => {
      if (chat.title) {
        const originalTitle = chat.title;

        // First replace keycloak IDs with names (for backward compatibility)
        if (keycloakIds.size > 0) {
          chat.title = this.chatbotProfileProviderService.replaceKeycloakIdsWithNames(chat.title);
        }

        // Then replace acronyms with names (for the new system)
        if (acronyms.size > 0) {
          chat.title = this.chatbotProfileProviderService.replaceAcronymsWithNames(chat.title);
        }

        // If title was updated, populate global cache from profiles
        if (originalTitle !== chat.title) {
          this.populateGlobalCacheFromProfiles(keycloakIds, acronyms);
        }
      }
    });
  }

  private populateGlobalCacheFromProfiles(keycloakIds: Set<string>, acronyms: Set<string>): void {
    // This method helps populate the global cache when we encounter new profiles
    // The actual caching is handled by the profile provider service
    if (keycloakIds.size > 0 || acronyms.size > 0) {
      // Trigger a markdown re-render if profiles were updated
      this.chatbotProfileProviderService.forceMarkdownReRender();
    }
  }

  // Keep the old method for backward compatibility but make it use the new method
  private populateGlobalCacheFromChats(chats: ChatbotChat[]): void {
    this.updateChatTitles(chats);
  }

  public refreshChatTitles(): void {
    if (this.currentChatList && this.currentChatList.length > 0) {
      this.updateChatTitles(this.currentChatList);
      this.groupChatsByDate();
    }
  }
}
